import { forwardRef } from 'react';
import styles from './LeafButton.module.css';

interface LeafButtonProps {
  onClick: () => void;
  ariaLabel: string;
}

/**
 * Floating gold leaf button — the single tap target at the bottom of
 * PrayerDetail that opens the Sacred Leaf action sheet. The leaf glyph
 * uses olive-style parallel venation (midrib + 4 fine veins running
 * roughly parallel to the midrib) — olive carries the deepest Christian
 * symbolic load for a prayer app: the dove brought an olive leaf to
 * Noah as the original answered prayer (Gen 8:11), olive oil
 * consecrates kings and the sick, "Christ" / "Messiah" literally means
 * "anointed one." The parallel-vein structure is also visually
 * distinctive — solves the prior generic-leaf reading.
 *
 * Sizing: 72×72 hit area, 60×68 visible glyph. Anchors against
 * MicButton lg (76) — the leaf is the sole primary affordance at the
 * bottom of PrayerDetail post-Sacred-Leaf-redesign, so it deserves a
 * footprint comparable to a primary mic without exactly matching
 * (the slight subordination signals "menu opener" vs "input capture").
 *
 * Halo: persistent breathing glow always plays via .button's animation
 * rule (no separate first-visit class). prefers-reduced-motion holds a
 * static mid-amplitude glow so the leaf still reads as primary.
 */
const LeafButton = forwardRef<HTMLButtonElement, LeafButtonProps>(function LeafButton(
  { onClick, ariaLabel },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={styles.button}
      onClick={onClick}
      aria-label={ariaLabel}
      // iOS: prevent the tap from stealing focus from anything underneath
      // (matches MicButton's onMouseDown handler).
      onMouseDown={(e) => e.preventDefault()}
    >
      <svg
        width="60"
        height="68"
        viewBox="0 0 52 60"
        fill="none"
        aria-hidden="true"
      >
        {/* Olive-shaped silhouette — same outline as the prior leaf
            (a vertical asymmetric teardrop, tip at bottom-center).
            Gold radial gradient body + soft outline. */}
        <path
          d="M24 56 Q18 48 10 38 C4 30 2 20 8 12 C12 6 20 4 28 6 C36 8 44 14 46 24 C48 34 42 44 34 52 Q30 56 26 58Z"
          fill="url(#leafBtnBody)"
        />
        <path
          d="M24 56 Q18 48 10 38 C4 30 2 20 8 12 C12 6 20 4 28 6 C36 8 44 14 46 24 C48 34 42 44 34 52 Q30 56 26 58Z"
          fill="none"
          stroke="rgba(150, 120, 70, 0.55)"
          strokeWidth="1"
        />
        {/* Olive parallel venation: midrib + 4 fine veins running
            roughly parallel to the midrib (inner pair stronger,
            outer pair softer). Outer pair lifted to 0.45 opacity to
            hedge against gold-on-gold wash on older OLED panels. */}
        <path
          d="M 23 8 Q 24 30 26 56"
          stroke="rgba(150, 120, 70, 0.78)"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 19 14 Q 19 30 22 50"
          stroke="rgba(150, 120, 70, 0.5)"
          strokeWidth="1"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 27 14 Q 28 30 28 50"
          stroke="rgba(150, 120, 70, 0.5)"
          strokeWidth="1"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 14 20 Q 16 32 20 48"
          stroke="rgba(150, 120, 70, 0.45)"
          strokeWidth="0.9"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 32 20 Q 32 32 30 48"
          stroke="rgba(150, 120, 70, 0.45)"
          strokeWidth="0.9"
          strokeLinecap="round"
          fill="none"
        />
        <defs>
          <radialGradient id="leafBtnBody" cx="35%" cy="30%" r="65%">
            <stop offset="0%" stopColor="rgba(232, 212, 160, 0.92)" />
            <stop offset="50%" stopColor="rgba(196, 162, 101, 0.85)" />
            <stop offset="100%" stopColor="rgba(150, 120, 70, 0.78)" />
          </radialGradient>
        </defs>
      </svg>
    </button>
  );
});

export default LeafButton;
